import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useAuthHttp } from '@hooks';
import { SUPPLIER_CREATE } from '@constants';
import { Supplier } from '@views/SupplierMaster/supplierRecord';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { Modal } from '../../tailwindComponents/Modal';
import { Button } from '../../tailwindComponents/Button';
import { SupplierFormFields, supplierFormSchema } from '../SupplierForm';
import { SupplierFormValues } from '../SupplierForm/types';
import { emptyLocation } from '../SupplierForm/SupplierFormFields';

interface CreateSupplierModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSupplierCreated: (supplier: Supplier) => void;
}

export function CreateSupplierModal({
  isOpen,
  onClose,
  onSupplierCreated,
}: CreateSupplierModalProps) {
  const authHttp = useAuthHttp();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const methods = useForm<SupplierFormValues>({
    resolver: yupResolver(supplierFormSchema),
    mode: 'onChange',
    defaultValues: {
      supplierId: '',
      name: '',
      type: '',
      contactName: '',
      contactEmail: '',
      contactNumber: '',
      hqId: '',
      hqName: '',
      locations: [{ ...emptyLocation }],
    },
  });

  const { formState, control, setValue, clearErrors, handleSubmit, reset } =
    methods;

  const handleOnSubmit = async (data: SupplierFormValues) => {
    setIsSubmitting(true);
    setErrorMessage(null);
    try {
      const { data: responseData } = await authHttp.post(
        SUPPLIER_CREATE,
        {
          supplier: data,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (responseData) {
        onSupplierCreated({
          ...data,
          id: responseData.supplier?.id || data.supplierId,
        } as Supplier);
        reset();
        onClose();
      }
    } catch (error: any) {
      setErrorMessage(
        error?.response?.data?.errors?.[0]?.message ||
          'Failed to create supplier. Please try again.'
      );
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    reset();
    setErrorMessage(null);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title='Create New Supplier'
      className='sm:max-w-4xl'
    >
      <form
        onSubmit={(e) => {
          e.stopPropagation();
          handleSubmit(handleOnSubmit)(e);
        }}
        noValidate
      >
        <p className='text-sm text-gray-500 font-inter mb-4'>
          Fill in the details below to add a new supplier. They&apos;ll be
          automatically selected once created.
        </p>
        <div className='max-h-[60vh] overflow-y-auto rounded-lg border border-gray-100 bg-gray-50/40 px-4 py-4'>
          <SupplierFormFields
            control={control}
            formState={formState}
            setValue={setValue}
            clearErrors={clearErrors}
          />
        </div>
        {errorMessage && (
          <div className='mt-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5'>
            <ExclamationTriangleIcon className='h-5 w-5 flex-shrink-0 text-red-500 mt-0.5' />
            <p className='text-sm text-red-700 font-inter'>{errorMessage}</p>
          </div>
        )}
        <div className='mt-5 flex justify-end gap-3 border-t border-gray-200 pt-4'>
          <Button
            onClick={handleClose}
            intent='secondary'
            size='sm'
            label='Cancel'
            type='button'
          />
          <Button
            intent='primary'
            size='sm'
            label={isSubmitting ? 'Creating...' : 'Create Supplier'}
            type='submit'
            disabled={isSubmitting}
          />
        </div>
      </form>
    </Modal>
  );
}
